import { useCallback, useEffect, useRef, useState } from "react";
import { FiSearch, FiUpload, FiX, FiTrash2 } from "react-icons/fi";
import parkingYardService from "../../../services/parkingYard.service";
import PaginationBar from "../../../components/common/PaginationBar";
import DownloadSampleExcelButton from "../../../components/common/DownloadSampleExcelButton";
import { PAGE_LIMIT } from "../../../constants/pagination";
import { readListItems, readPagination } from "../../../utils/listPagination";
import { saveExcelBlob, getApiErrorMessage } from "../../../utils/downloadExcel";
import "../../../styles/users.css";
import "../../../styles/bankDetails.css";
import "../../../styles/parkingYardAdmin.css";

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function SsdiUploadParkingYard() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [yards, setYards] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [pendingId, setPendingId] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  const loadYards = useCallback(async () => {
    try {
      setLoading(true);
      setListError("");
      const res = await parkingYardService.list({
        q: debouncedQuery || undefined,
        page,
        limit: PAGE_LIMIT,
      });
      const payload = res?.data || {};
      setYards(readListItems(payload, ["yards", "items"]));
      setPagination(readPagination(payload));
    } catch (err) {
      setYards([]);
      setListError(err?.response?.data?.message || "Failed to load parking yards.");
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, page]);

  useEffect(() => {
    loadYards();
  }, [loadYards]);

  const acceptFile = (next) => {
    if (!next) return;
    const name = String(next.name || "").toLowerCase();
    if (!/\.(xlsx|xls|csv)$/.test(name)) {
      setError("Only .xlsx, .xls, and .csv files are allowed.");
      return;
    }
    setError("");
    setSummary(null);
    setFile(next);
  };

  const handleUpload = async () => {
    if (!file || uploading) return;
    setUploading(true);
    setError("");
    setSummary(null);
    try {
      const res = await parkingYardService.upload(file);
      setSummary(res?.data?.data || res?.data || null);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await loadYards();
    } catch (err) {
      setError(await getApiErrorMessage(err, "Parking yard upload failed."));
    } finally {
      setUploading(false);
    }
  };

  const handleSample = async () => {
    try {
      const response = await parkingYardService.downloadSample();
      await saveExcelBlob(response, "parking-yard-sample.xlsx");
    } catch (err) {
      setError(await getApiErrorMessage(err, "Failed to download sample Excel."));
    }
  };

  const toggleActive = async (yard) => {
    setPendingId(yard.id);
    try {
      await parkingYardService.setStatus(yard.id, !yard.isActive);
      await loadYards();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to update status.");
    } finally {
      setPendingId("");
    }
  };

  const handleDelete = async (yard) => {
    if (!window.confirm(`Delete parking yard "${yard.name}"? This cannot be undone.`)) return;
    setPendingId(yard.id);
    try {
      await parkingYardService.remove(yard.id);
      await loadYards();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete parking yard.");
    } finally {
      setPendingId("");
    }
  };

  return (
    <div className="page py-admin">
      <header className="py-admin__header">
        <div>
          <h2>Upload Parking Yard</h2>
          <p className="muted">
            Excel columns: Name, Address, Area, Pincode, Owner Name, Owner Mobile No, Manager Name, Manager Mobile No,
            Website, Email, Yard Code, Type of Yard, Location.
          </p>
        </div>
        <DownloadSampleExcelButton
          showFieldSelect={false}
          label="Download sample Excel"
          onDownload={handleSample}
        />
      </header>

      <div
        className={`py-drop${dragOver ? " py-drop--over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          acceptFile(e.dataTransfer.files?.[0]);
        }}
      >
        <FiUpload size={16} />
        <p>
          <strong>Drag &amp; Drop Excel File</strong>
          <span>or</span>
          <button type="button" className="secondary-page-btn" onClick={() => inputRef.current?.click()}>
            Choose File
          </button>
        </p>
        <span className="muted">Supported: XLSX, XLS</span>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          hidden
          onChange={(e) => acceptFile(e.target.files?.[0])}
        />
      </div>

      {file ? (
        <p className="py-file">
          {file.name} · {formatBytes(file.size)}
        </p>
      ) : null}

      <button
        type="button"
        className="primary-page-btn"
        disabled={!file || uploading}
        onClick={handleUpload}
      >
        {uploading ? "Uploading…" : "Upload Parking Yard"}
      </button>

      {error ? <p className="error-text">{error}</p> : null}

      {summary ? (
        <div className="py-summary">
          <h3>Parking Yard Upload Completed</h3>
          <ul>
            <li>
              <span>Total Rows</span>
              <strong>{Number(summary.totalRows || 0).toLocaleString("en-IN")}</strong>
            </li>
            <li>
              <span>Inserted</span>
              <strong>{Number(summary.inserted || 0).toLocaleString("en-IN")}</strong>
            </li>
            <li>
              <span>Updated</span>
              <strong>{Number(summary.updated || 0).toLocaleString("en-IN")}</strong>
            </li>
            <li>
              <span>Skipped</span>
              <strong>{Number(summary.skipped || 0).toLocaleString("en-IN")}</strong>
            </li>
            <li>
              <span>Errors</span>
              <strong>{Number(summary.errors || 0).toLocaleString("en-IN")}</strong>
            </li>
          </ul>
          {Array.isArray(summary.errorRows) && summary.errorRows.length ? (
            <div className="py-errors">
              {summary.errorRows.map((row) => (
                <p key={`${row.row}-${row.reason}`}>
                  Row {row.row} - {row.reason}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <section className="py-admin__list bd-table-card">
        <h3>Parking Yard Records</h3>
        <form
          className="py-search"
          onSubmit={(e) => {
            e.preventDefault();
            setDebouncedQuery(searchQuery.trim());
            setPage(1);
          }}
        >
          <div className="py-search__field">
            <FiSearch aria-hidden="true" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, area or pincode"
              aria-label="Search parking yards"
            />
            {searchQuery ? (
              <button
                type="button"
                className="py-search__clear"
                aria-label="Clear search"
                onClick={() => setSearchQuery("")}
              >
                <FiX />
              </button>
            ) : null}
          </div>
        </form>

        {listError ? <p className="error-text">{listError}</p> : null}
        {loading ? (
          <p className="muted">Loading parking yards…</p>
        ) : yards.length === 0 ? (
          <p className="muted">No parking yards found.</p>
        ) : (
          <div className="company-table-wrap bd-excel-sheet">
            <table className="users-table excel-grid-table bd-excel-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Area</th>
                  <th>Pincode</th>
                  <th>Owner Name</th>
                  <th>Owner Mobile No</th>
                  <th>Manager Name</th>
                  <th>Manager Mobile No</th>
                  <th>Website</th>
                  <th>Email</th>
                  <th>Yard Code</th>
                  <th>Type of Yard</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {yards.map((yard, index) => (
                  <tr key={yard.id}>
                    <td>{((pagination.page || page) - 1) * PAGE_LIMIT + index + 1}</td>
                    <td>{yard.name}</td>
                    <td>{yard.address || "—"}</td>
                    <td>{yard.area || "—"}</td>
                    <td>{yard.pincode || "—"}</td>
                    <td>{yard.ownerName || "—"}</td>
                    <td>{yard.ownerMobile || yard.phone || "—"}</td>
                    <td>{yard.managerName || "—"}</td>
                    <td>{yard.managerPhone || yard.managerMobile || "—"}</td>
                    <td>
                      {yard.website ? (
                        <a href={yard.website} target="_blank" rel="noopener noreferrer">
                          {yard.website}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{yard.email || "—"}</td>
                    <td>{yard.yardCode || "—"}</td>
                    <td>{yard.yardType || "—"}</td>
                    <td>
                      {yard.mapsUrl || yard.location ? (
                        <a
                          href={yard.mapsUrl || yard.location}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View on Map
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="bd-col-status">
                      <span
                        className={`bd-status-badge ${
                          yard.isActive ? "bd-status-badge--uploaded" : "bd-status-badge--upload"
                        }`}
                      >
                        {yard.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="py-actions">
                        <button
                          type="button"
                          className="secondary-page-btn py-delete"
                          disabled={pendingId === yard.id}
                          onClick={() => handleDelete(yard)}
                        >
                          <FiTrash2 /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <PaginationBar
          page={pagination.page || page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          loading={loading}
          onPageChange={setPage}
        />
      </section>
    </div>
  );
}
